/**
 * Disaster-recovery backup.
 *
 * One Excel workbook, one sheet per dataset, every column the API returns —
 * not the curated export columns. The point is that if the app or the database
 * goes down, an admin still has the records on their own machine, so nothing
 * is trimmed for readability.
 *
 * It runs itself on the first admin login of each 4-month cycle (Jan-Apr,
 * May-Aug, Sep-Dec) and can be triggered by hand any time from the user panel.
 */
import { leadsAPI, dashboardAPI } from '../services/api';

export const BACKUP_CYCLE_KEY = 'ags_backup_last_cycle';

// Jan-Apr = C1, May-Aug = C2, Sep-Dec = C3. Stored as e.g. "2026-C3", which is
// all the state the schedule needs: a different string means a new cycle.
export const cycleKeyOf = (date = new Date()) =>
  `${date.getFullYear()}-C${Math.floor(date.getMonth() / 4) + 1}`;

export const cycleLabelOf = (date = new Date()) => {
  const first = Math.floor(date.getMonth() / 4) * 4;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[first]}-${months[first + 3]} ${date.getFullYear()}`;
};

const readStore = (key) => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const writeStore = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch { /* private mode — just means it runs again */ }
};

export const backupIsDueNow = (now = new Date()) => readStore(BACKUP_CYCLE_KEY) !== cycleKeyOf(now);

// Excel refuses a cell longer than 32767 characters, and company_overview /
// contact_book can exceed that. Truncating one cell is better than a workbook
// that will not open.
const CELL_LIMIT = 32000;

const cellValue = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    try { return JSON.stringify(v).slice(0, CELL_LIMIT); } catch { return '[unserialisable]'; }
  }
  if (typeof v === 'string' && v.length > CELL_LIMIT) return v.slice(0, CELL_LIMIT) + '…[truncated]';
  return v;
};

// Rows come back from different endpoints and not every row carries every key,
// so the header is the union across the whole set — a column present on one
// record only must still survive the round trip.
const toSheetRows = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const columns = [];
  const seen = new Set();
  list.forEach(r => Object.keys(r || {}).forEach(k => {
    if (!seen.has(k)) { seen.add(k); columns.push(k); }
  }));
  return list.map(r => {
    const out = {};
    columns.forEach(c => { out[c] = cellValue(r ? r[c] : ''); });
    return out;
  });
};

const unwrap = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

// Sheet name, loader. Order is the order of the tabs in the app.
const DATASETS = [
  // The archive wants every column, not the trimmed list the screens use.
  { sheet: 'Pre-Sales', load: () => leadsAPI.getAllFull() },
  { sheet: 'Post-Sales', load: () => dashboardAPI.getPostSales({}) },
  { sheet: 'Potential Clients', load: () => dashboardAPI.getPotentialClients({}) },
  { sheet: 'Pipeline Clients', load: () => dashboardAPI.getPipelineClients({}) },
  { sheet: 'Onboarded Clients', load: () => dashboardAPI.getOnboardedClients({}) }
];

/**
 * Fetch everything, build the workbook and hand it to the browser.
 * Resolves to { fileName, counts, failures }. A dataset that fails to load
 * still leaves the rest of the workbook intact — a partial backup beats none —
 * and is named both in the Backup Info sheet and in the return value.
 */
export const downloadBackupWorkbook = async ({ trigger = 'manual' } = {}) => {
  const XLSX = await import('xlsx');
  const startedAt = new Date();

  const settled = await Promise.allSettled(DATASETS.map(d => d.load()));
  const counts = {};
  const failures = [];
  const sheets = settled.map((res, i) => {
    const { sheet } = DATASETS[i];
    if (res.status === 'fulfilled') {
      const rows = toSheetRows(unwrap(res.value));
      counts[sheet] = rows.length;
      return { sheet, rows };
    }
    const reason = res.reason?.response?.data?.error || res.reason?.message || 'request failed';
    failures.push({ sheet, reason });
    counts[sheet] = 0;
    return { sheet, rows: [] };
  });

  if (failures.length === DATASETS.length) {
    throw new Error(`Backup failed — no data could be fetched (${failures[0].reason})`);
  }

  const wb = XLSX.utils.book_new();

  // A cover sheet, so a file found on a drive months later explains itself.
  const info = [
    { Field: 'Generated', Value: startedAt.toLocaleString() },
    { Field: 'Cycle', Value: cycleLabelOf(startedAt) },
    { Field: 'Trigger', Value: trigger === 'auto' ? 'Automatic (new 4-month cycle)' : 'Manual download' },
    ...sheets.map(s => ({ Field: `${s.sheet} rows`, Value: counts[s.sheet] })),
    ...failures.map(f => ({ Field: `FAILED: ${f.sheet}`, Value: f.reason }))
  ];
  const infoWs = XLSX.utils.json_to_sheet(info);
  infoWs['!cols'] = [{ wch: 24 }, { wch: 46 }];
  XLSX.utils.book_append_sheet(wb, infoWs, 'Backup Info');

  sheets.forEach(({ sheet, rows }) => {
    const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ '(no records)': '' }]);
    ws['!cols'] = (rows.length ? Object.keys(rows[0]) : ['(no records)']).map(() => ({ wch: 22 }));
    XLSX.utils.book_append_sheet(wb, ws, sheet);
  });

  const stamp = startedAt.toISOString().split('T')[0];
  const fileName = `AGS_Sales_Hub_backup_${cycleKeyOf(startedAt)}_${stamp}.xlsx`;
  XLSX.writeFile(wb, fileName);

  return { fileName, counts, failures };
};

/**
 * The scheduled half: download once per 4-month cycle, for admins only.
 * The cycle is recorded only after the file is actually written, so a failed
 * or blocked download is retried at the next login rather than silently skipped.
 */
export const runScheduledBackup = async ({ isAdmin }) => {
  if (!isAdmin) return { ran: false, reason: 'not-admin' };
  const cycle = cycleKeyOf();
  if (readStore(BACKUP_CYCLE_KEY) === cycle) return { ran: false, reason: 'already-done' };
  const result = await downloadBackupWorkbook({ trigger: 'auto' });
  writeStore(BACKUP_CYCLE_KEY, cycle);
  return { ran: true, ...result };
};
